import { useEffect, useState } from 'react';
import { api } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import type { AdminSetHoursRequest } from '../types/admin-garage-edit';
import { closesNextDay, isAllDay } from '../utils/garageHours';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

interface EditGarageHoursDialogProps {
  garageId: string;
  initial: AdminSetHoursRequest[];
  onClose: () => void;
  onSaved: () => void;
}

export default function EditGarageHoursDialog({ garageId, initial, onClose, onSaved }: EditGarageHoursDialogProps) {
  const { token } = useAuth();
  const [slots, setSlots] = useState<AdminSetHoursRequest[]>(() => {
    const byDay = new Map(initial.map((s) => [s.dayOfWeek, s]));
    return Array.from({ length: 7 }, (_, i) => byDay.get(i + 1) ?? { dayOfWeek: i + 1, openTime: '09:00', closeTime: '18:00', isClosed: true });
  });
  const [saving, setSaving] = useState(false);
  // #1385: the "24 hours" box only shows once the 24-hour switch is on (older apps show such a day as
  // Closed). Any failure reads as off.
  const [allDayEnabled, setAllDayEnabled] = useState(false);
  useEffect(() => {
    if (!token) return;
    api.adminGarageHoursSettings(garageId, token)
      .then((s) => setAllDayEnabled(!!s?.allDayEnabled))
      .catch(() => setAllDayEnabled(false));
  }, [garageId, token]);
  const [error, setError] = useState<string | null>(null);

  const update = (idx: number, patch: Partial<AdminSetHoursRequest>) => {
    setSlots((prev) => prev.map((s, i) => i === idx ? { ...s, ...patch } : s));
    setError(null);
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    // #1385: no open-before-close check. A close before the open runs past midnight (05:00–02:00 =
    // until 2 am the next day); equal times are open 24 hours.
    for (const s of slots) {
      if (!s.isClosed && (!s.openTime || !s.closeTime)) {
        setError(`${DAYS[s.dayOfWeek - 1]}: set both an open and a close time`);
        return;
      }
      if (!s.isClosed && !allDayEnabled && isAllDay(s.openTime, s.closeTime)) {
        setError(`${DAYS[s.dayOfWeek - 1]}: open and close times must differ`);
        return;
      }
    }
    setSaving(true);
    setError(null);
    try {
      await api.adminGarageUpdateHours(garageId, slots, token);
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update hours');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-modal-overlay" role="dialog" aria-modal="true">
      <form className="admin-modal" onSubmit={onSubmit}>
        <div className="admin-modal-header"><h3>Edit Operating Hours</h3></div>
        <div className="admin-modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {slots.map((slot, idx) => (
            <div key={slot.dayOfWeek} style={{ display: 'grid', gridTemplateColumns: allDayEnabled ? '60px 1fr 1fr auto auto' : '60px 1fr 1fr auto', gap: '0.5rem', alignItems: 'center' }}>
              <span>{DAYS[slot.dayOfWeek - 1]}</span>
              <input className="admin-input" type="time" aria-label={`${DAYS[slot.dayOfWeek - 1]} open`} value={slot.openTime} disabled={slot.isClosed || (allDayEnabled && isAllDay(slot.openTime, slot.closeTime))} onChange={(e) => update(idx, { openTime: e.target.value })} />
              <span style={{ display: 'flex', flexDirection: 'column' }}>
                <input className="admin-input" type="time" aria-label={`${DAYS[slot.dayOfWeek - 1]} close`} value={slot.closeTime} disabled={slot.isClosed || (allDayEnabled && isAllDay(slot.openTime, slot.closeTime))} onChange={(e) => update(idx, { closeTime: e.target.value })} />
                {!slot.isClosed && closesNextDay(slot.openTime, slot.closeTime) && (
                  <small style={{ color: '#6b7280' }}>next day</small>
                )}
              </span>
              {allDayEnabled && (
                <label style={{ fontSize: '0.875rem' }}>
                  <input type="checkbox" checked={!slot.isClosed && isAllDay(slot.openTime, slot.closeTime)} disabled={slot.isClosed}
                    onChange={(e) => update(idx, e.target.checked ? { openTime: '00:00', closeTime: '00:00' } : { openTime: '09:00', closeTime: '18:00' })} /> 24 hours
                </label>
              )}
              <label style={{ fontSize: '0.875rem' }}>
                <input type="checkbox" checked={!!slot.isClosed} onChange={(e) => update(idx, { isClosed: e.target.checked })} /> Closed
              </label>
            </div>
          ))}
          {error && <div className="admin-error">{error}</div>}
        </div>
        <div className="admin-modal-footer">
          <button type="button" className="admin-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="submit" className="admin-btn admin-btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </form>
    </div>
  );
}
